import React from 'react';
import styled from 'styled-components';
import completeIcon from '../../../static/icons/complete-icon.svg';
import darkCheckCircleIcon from '../../../static/icons/dark-check-circle.svg';
import {
  computeFeedbackGenerationProgress,
  deriveFeedbackGenerationStepsFromProgress,
  FEEDBACK_GENERATION_STEP_STATUS,
} from './constants';

export {
  DEFAULT_FEEDBACK_GENERATION_STEPS,
  FEEDBACK_GENERATION_STEP_STATUS,
  createFeedbackGenerationSteps,
  computeFeedbackGenerationProgress,
  deriveFeedbackGenerationStepsFromProgress,
} from './constants';

export const FeedbackGenerationProgress = ({
  title = 'Feedback is being generated',
  steps = [],
  progress,
  // Real counts off the status endpoint, e.g. "12 of 30 ready". A simulated bar
  // over a genuinely observable process is a lie the status endpoint made
  // unnecessary, so this sits where the animation used to.
  message,
  failures = [],
  // Where a stalled or refused run can go next. Supplied by the caller because
  // this screen is shared and only the caller knows what "back" means for it.
  actions = null,
  fullPage = true,
  className,
}) => {
  const resolvedSteps =
    steps.length > 0
      ? steps
      : typeof progress === 'number'
        ? deriveFeedbackGenerationStepsFromProgress(progress)
        : deriveFeedbackGenerationStepsFromProgress(0);

  const resolvedProgress =
    typeof progress === 'number'
      ? Math.min(100, Math.max(0, progress))
      : computeFeedbackGenerationProgress(resolvedSteps);

  return (
    <Root className={className} $fullPage={fullPage}>
      <Content>
        <ProgressTrack>
          <ProgressFill $value={resolvedProgress} />
        </ProgressTrack>
        <Title>{title}</Title>
        {message && <Message>{message}</Message>}
        {actions && <Actions>{actions}</Actions>}
        {failures.length > 0 && (
          <FailureList>
            {failures.map((failure) => (
              <FailureRow key={failure.submissionId || failure.studentId}>
                <FailureName>{failure.studentName}</FailureName>
                <FailureReason>{failure.reason}</FailureReason>
              </FailureRow>
            ))}
          </FailureList>
        )}
        <StepList>
          {resolvedSteps.map((step) => {
            const isComplete = step.status === FEEDBACK_GENERATION_STEP_STATUS.COMPLETE;
            return (
              <StepRow key={step.id}>
                <StepIcon>
                  <img
                    src={isComplete ? completeIcon : darkCheckCircleIcon}
                    alt=""
                    width={20}
                    height={20}
                    aria-hidden="true"
                  />
                </StepIcon>
                <StepLabel>{step.label}</StepLabel>
              </StepRow>
            );
          })}
        </StepList>
      </Content>
    </Root>
  );
};

const Root = styled.div`
  display: grid;
  place-items: center;
  width: 100%;
  flex: 1 1 auto;
  align-self: stretch;
  min-height: ${({ $fullPage }) => ($fullPage ? '100vh' : '100%')};
  height: 100%;
  padding: 48px 24px;
  box-sizing: border-box;
  background: ${({ $fullPage }) => ($fullPage ? '#fff' : 'transparent')};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
`;

const ProgressTrack = styled.div`
  width: min(100%, 320px);
  height: 8px;
  border-radius: 999px;
  background: #e8e6eb;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $value }) => $value}%;
  border-radius: inherit;
  background: #7200e0;
  transition: width 0.45s ease;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 32px 0 28px;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 28px;
  font-weight: 600;
  line-height: 1.25;
  color: #141414;
  text-align: center;
`;

const Message = styled.p`
  margin: -16px 0 24px;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 15px;
  line-height: 1.4;
  color: #6b7280;
  text-align: center;
`;

// A skipped student used to be dropped with no user-visible trace. Naming them
// here is the whole reason the status endpoint reports FAILED at all.
const FailureList = styled.ul`
  list-style: none;
  margin: 0 0 24px;
  padding: 12px 16px;
  width: min(100%, 420px);
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid rgba(201, 198, 204, 0.6);
  background: #fdf6f6;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FailureRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
`;

const FailureName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #302e32;
`;

const FailureReason = styled.span`
  font-size: 13px;
  line-height: 1.35;
  color: #8a5a5a;
`;

const StepList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  width: min(100%, 320px);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepRow = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepIcon = styled.span`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const StepLabel = styled.span`
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 15px;
  line-height: 1.4;
  color: #302e32;
  font-weight: 500;
`;
